import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Tree,
  Row,
  Col,
  message,
  Popconfirm,
  Spin,
  Empty,
  Badge,
  Divider,
  App,
  Upload,
  Alert,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UserOutlined,
  BankOutlined,
  ShopOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import * as XLSXStyle from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import type { DataNode } from 'antd/es/tree';
import type { ColumnsType } from 'antd/es/table';
import { OrganizationService, type CompanyWithBranches, type BranchWithUsers } from '../services/organizationService';
import { UserService } from '../services/userService';
import type { Database } from '../types/database.types';
import { supabase } from '../services/supabase';
import './OrganizationManagement.css';
import { logger } from '../utils/logger';

type Company = Database['public']['Tables']['companies']['Row'];
type Branch = Database['public']['Tables']['branches']['Row'];
type User = Database['public']['Tables']['users']['Row'];

interface CompanyFormData {
  name: string;
}

interface BranchFormData {
  branch_name: string;
  company_id: string;
  address?: string;
}

const OrganizationManagement: React.FC = () => {
  const { notification } = App.useApp();
  const [companies, setCompanies] = useState<CompanyWithBranches[]>([]);
  const [branches, setBranches] = useState<BranchWithUsers[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string>('all');
  const [selectedNodeType, setSelectedNodeType] = useState<'all' | 'company' | 'branch'>('all');
  const [branchPagination, setBranchPagination] = useState({ current: 1, pageSize: 10 });
  const [userPagination, setUserPagination] = useState({ current: 1, pageSize: 10 });
  
  // Modal states
  const [companyModalVisible, setCompanyModalVisible] = useState(false);
  const [branchModalVisible, setBranchModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [excelUploadModalVisible, setExcelUploadModalVisible] = useState(false);
  const [excelData, setExcelData] = useState<any[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedCompanyForExcel, setSelectedCompanyForExcel] = useState<string>('');
  const [isCompanyPreselected, setIsCompanyPreselected] = useState(false);  // 조직도에서 선택된 경우 true
  const [duplicatesInFile, setDuplicatesInFile] = useState<Set<string>>(new Set());
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'idle' | 'success' | 'warning' | 'error';
    message?: string;
    duplicates?: string[];
  }>({ type: 'idle' });
  const [uploadKey, setUploadKey] = useState(0); // Upload 컴포넌트 리셋용 key
  
  // Forms
  const [companyForm] = Form.useForm();
  const [branchForm] = Form.useForm();

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadData();
    checkAuth();
  }, []);
  
  // 권한 확인
  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', user.id)
        .single();
    }
  };

  // 실시간 구독 설정
  useEffect(() => {
    const companiesSubscription = supabase
      .channel('companies-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'companies' }, () => {
        loadData();
      })
      .subscribe();

    const branchesSubscription = supabase
      .channel('branches-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'branches' }, () => {
        loadData();
      })
      .subscribe();

    const usersSubscription = supabase
      .channel('users-org-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'users' }, () => {
        loadData();
      })
      .subscribe();

    return () => {
      companiesSubscription.unsubscribe();
      branchesSubscription.unsubscribe();
      usersSubscription.unsubscribe();
    };
  }, []);

  // 현재 사용자 정보 상태
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // 데이터 로드
  const loadData = async () => {
    setLoading(true);
    try {
      // 현재 사용자 정보 가져오기
      const { data: { user } } = await supabase.auth.getUser();
      let userData: User | null = null;
      if (user) {
        const { data } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', user.id)
          .single();
        userData = data;
        setCurrentUser(userData);
      }

      const [companiesData, branchesData, usersData] = await Promise.all([
        OrganizationService.getCompanies(),
        OrganizationService.getBranches(),
        UserService.getUsers()
      ]);

      // 일반 관리자는 자신의 회사/지점만 볼 수 있음
      let filteredCompanies = companiesData;
      let filteredBranches = branchesData;
      let filteredUsers = usersData;

      if (userData && !userData.is_admin && userData.has_admin_role && userData.company_id) {
        // 일반 관리자인 경우 자신의 회사만 필터링
        const userCompanyId = userData.company_id;
        filteredCompanies = companiesData.filter(c => c.id === userCompanyId);
        filteredBranches = branchesData.filter(b => b.company_id === userCompanyId);
        filteredUsers = usersData.filter(u => u.company_id === userCompanyId);
      }

      setCompanies(filteredCompanies);
      setBranches(filteredBranches);
      
      // 슈퍼관리자를 제외한 활성 사용자만 표시, 일반 관리자를 먼저 정렬
      const activeUsers = filteredUsers.filter(u => u.is_active && !u.is_deleted && !u.is_admin);
      const sortedUsers = activeUsers.sort((a, b) => {
        // 일반 관리자를 최상단으로
        if (a.has_admin_role && !b.has_admin_role) return -1;
        if (!a.has_admin_role && b.has_admin_role) return 1;
        return 0;
      });
      setUsers(sortedUsers);
    } catch (error) {
      logger.error('Error loading data:', error);
      message.error('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 트리 데이터 생성
  const getTreeData = (): DataNode[] => {
    const totalUsers = users.length;
    
    const treeData: DataNode[] = [{
      title: `전체 조직 (${totalUsers}명)`,
      key: 'all',
      icon: <BankOutlined />,
      children: companies.map(company => ({
        title: `${company.name} (${company.userCount || 0}명)`,
        key: `company-${company.id}`,
        icon: <TeamOutlined />,
        children: branches
          .filter(branch => branch.company_id === company.id)
          .map(branch => ({
            title: `${branch.branch_name} (${branch.userCount || 0}명)`,
            key: `branch-${branch.id}`,
            icon: <ShopOutlined />,
          }))
      }))
    }];

    return treeData;
  };

  // 회사 테이블 컬럼
  const companyColumns: ColumnsType<CompanyWithBranches> = [
    {
      title: '회사명',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <TeamOutlined />
          <strong>{text}</strong>
          {record.company_code && <Tag>{record.company_code}</Tag>}
        </Space>
      ),
    },
    {
      title: '지점 수',
      dataIndex: 'branchCount',
      key: 'branchCount',
      width: 100,
      align: 'center',
      render: (count) => `${count || 0}개`,
    },
    {
      title: '사용자 수',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 100,
      align: 'center',
      render: (count) => `${count || 0}명`,
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: '작업',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          {currentUser?.is_admin && (
            <>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditCompany(record)}
              >
                수정
              </Button>
              <Popconfirm
                title="회사 삭제"
                description={
                  record.branchCount || record.userCount
                    ? "이 회사에는 지점이나 사용자가 있습니다. 먼저 모든 지점과 사용자를 삭제해주세요."
                    : "정말 이 회사를 삭제하시겠습니까?"
                }
                onConfirm={() => handleDeleteCompany(record.id)}
                okText="삭제"
                cancelText="취소"
                disabled={!!(record.branchCount || record.userCount)}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!!(record.branchCount || record.userCount)}
                >
                  삭제
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 지점 테이블 컬럼
  const branchColumns: ColumnsType<BranchWithUsers> = [
    {
      title: '지점명',
      dataIndex: 'branch_name',
      key: 'branch_name',
      render: (text, record) => (
        <Space>
          <ShopOutlined />
          <strong>{text}</strong>
          {record.branch_code && <Tag>{record.branch_code}</Tag>}
        </Space>
      ),
    },
    {
      title: '소속 회사',
      key: 'company',
      render: (_, record) => record.company?.name || '-',
    },
    {
      title: '주소',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: '사용자 수',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 100,
      align: 'center',
      render: (count) => `${count || 0}명`,
    },
    {
      title: '작업',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          {currentUser?.is_admin && (
            <>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditBranch(record)}
              >
                수정
              </Button>
              <Popconfirm
                title="지점 삭제"
                description={
                  record.userCount
                    ? "이 지점에는 사용자가 있습니다. 먼저 모든 사용자를 다른 지점으로 이동하거나 삭제해주세요."
                    : "정말 이 지점을 삭제하시겠습니까?"
                }
                onConfirm={() => handleDeleteBranch(record.id)}
                okText="삭제"
                cancelText="취소"
                disabled={!!record.userCount}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!!record.userCount}
                >
                  삭제
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 사용자 테이블 컬럼
  const userColumns: ColumnsType<User> = [
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: '전화번호',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: '회사',
      key: 'company',
      render: (_, record) => {
        const company = companies.find(c => c.id === record.company_id);
        return company?.name || '-';
      },
    },
    {
      title: '지점',
      key: 'branch',
      render: (_, record) => {
        const branch = branches.find(b => b.id === record.branch_id);
        return branch?.branch_name || '-';
      },
    },
    {
      title: '권한',
      key: 'role',
      render: (_, record) => (
        <Tag color={record.has_admin_role ? 'gold' : 'default'}>
          {record.has_admin_role ? '관리자' : '일반'}
        </Tag>
      ),
    },
    {
      title: '상태',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.is_active ? 'green' : 'red'}>
          {record.is_active ? '활성' : '비활성'}
        </Tag>
      ),
    },
  ];

  // 트리 선택 핸들러
  const handleTreeSelect = (selectedKeys: React.Key[]) => {
    if (selectedKeys.length > 0) {
      const key = selectedKeys[0] as string;
      setSelectedNode(key);
      
      if (key === 'all') {
        setSelectedNodeType('all');
      } else if (key.startsWith('company-')) {
        setSelectedNodeType('company');
      } else if (key.startsWith('branch-')) {
        setSelectedNodeType('branch');
      }
    }
  };

  // 필터링된 데이터
  const getFilteredData = () => {
    let filteredCompanies = companies;
    let filteredBranches = branches;
    let filteredUsers = users;

    if (selectedNode !== 'all') {
      if (selectedNode.startsWith('company-')) {
        const companyId = selectedNode.replace('company-', '');
        filteredCompanies = companies.filter(c => c.id === companyId);
        filteredBranches = branches.filter(b => b.company_id === companyId);
        filteredUsers = users.filter(u => u.company_id === companyId);
      } else if (selectedNode.startsWith('branch-')) {
        const branchId = selectedNode.replace('branch-', '');
        const branch = branches.find(b => b.id === branchId);
        if (branch) {
          filteredCompanies = companies.filter(c => c.id === branch.company_id);
          filteredBranches = branches.filter(b => b.id === branchId);
          filteredUsers = users.filter(u => u.branch_id === branchId);
        }
      }
    }

    // 필터링된 사용자도 관리자가 최상단에 오도록 정렬
    filteredUsers = filteredUsers.sort((a, b) => {
      if (a.has_admin_role && !b.has_admin_role) return -1;
      if (!a.has_admin_role && b.has_admin_role) return 1;
      return 0;
    });

    return { filteredCompanies, filteredBranches, filteredUsers };
  };

  // 회사 관련 핸들러
  const handleAddCompany = () => {
    companyForm.resetFields();
    setEditingCompany(null);
    setCompanyModalVisible(true);
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    companyForm.setFieldsValue({
      name: company.name,
    });
    setCompanyModalVisible(true);
  };

  const handleSaveCompany = async (values: CompanyFormData) => {
    try {
      if (editingCompany) {
        const updateData = {
          name: values.name
        };
        await OrganizationService.updateCompany(editingCompany.id, updateData);
        notification.success({
          message: '수정 완료',
          description: '회사 정보가 수정되었습니다.',
        });
      } else {
        await OrganizationService.createCompany(values);
        notification.success({
          message: '추가 완료',
          description: '새 회사가 추가되었습니다.',
        });
      }
      setCompanyModalVisible(false);
      await loadData();
    } catch (error: any) {
      logger.error('회사 저장 오류:', error);
      message.error(error.message || '작업 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    try {
      await OrganizationService.deleteCompany(companyId);
      notification.success({
        message: '삭제 완료',
        description: '회사가 삭제되었습니다.',
      });
      await loadData();
    } catch (error: any) {
      logger.error('회사 삭제 오류:', error);
      message.error(error.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  // 지점 관련 핸들러
  const handleAddBranch = () => {
    branchForm.resetFields();
    setEditingBranch(null);
    
    // 선택된 회사가 있으면 자동 설정
    if (selectedNode.startsWith('company-')) {
      const companyId = selectedNode.replace('company-', '');
      branchForm.setFieldsValue({ company_id: companyId });
    } else if (selectedNode.startsWith('branch-')) {
      // 지점이 선택된 경우 해당 지점의 회사를 자동 설정
      const branchId = selectedNode.replace('branch-', '');
      const branch = branches.find(b => b.id === branchId);
      if (branch) {
        branchForm.setFieldsValue({ company_id: branch.company_id });
      }
    }
    
    setBranchModalVisible(true);
  };

  const handleEditBranch = (branch: Branch) => {
    setEditingBranch(branch);
    branchForm.setFieldsValue({
      branch_name: branch.branch_name,
      company_id: branch.company_id,
      address: branch.address,
    });
    setBranchModalVisible(true);
  };

  const handleSaveBranch = async (values: BranchFormData) => {
    try {
      if (editingBranch) {
        const { company_id, ...updateData } = values;
        await OrganizationService.updateBranch(editingBranch.id, updateData);
        notification.success({
          message: '수정 완료',
          description: '지점 정보가 수정되었습니다.',
        });
      } else {
        await OrganizationService.createBranch(values);
        notification.success({
          message: '추가 완료',
          description: '새 지점이 추가되었습니다.',
        });
      }
      setBranchModalVisible(false);
      await loadData();
    } catch (error: any) {
      logger.error('지점 저장 오류:', error);
      message.error(error.message || '작업 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteBranch = async (branchId: string) => {
    try {
      await OrganizationService.deleteBranch(branchId);
      notification.success({
        message: '삭제 완료',
        description: '지점이 삭제되었습니다.',
      });
      await loadData();
    } catch (error: any) {
      logger.error('지점 삭제 오류:', error);
      message.error(error.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  // 샘플 엑셀 다운로드
  const handleDownloadSample = () => {
    // 정확히 A1:B1만 있는 워크시트 생성
    const ws: any = {};
    
    // A1, B1 셀만 정의 (헤더에 테두리 추가)
    ws['A1'] = { 
      v: '지점명',
      t: 's',
      s: {
        fill: {
          patternType: 'solid',
          fgColor: { rgb: 'E0E0E0' }
        },
        font: { bold: true },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      }
    };
    
    ws['B1'] = { 
      v: '주소',
      t: 's',
      s: {
        fill: {
          patternType: 'solid',
          fgColor: { rgb: 'E0E0E0' }
        },
        font: { bold: true },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      }
    };
    
    // 범위를 정확히 A1:B1로 제한
    ws['!ref'] = 'A1:B1';
    
    // 컬럼 너비 설정 (2개만)
    ws['!cols'] = [
      { wch: 20 }, // A열
      { wch: 40 }  // B열
    ];
    
    // 워크북 생성
    const wb = XLSXStyle.utils.book_new();
    
    // 시트 추가
    XLSXStyle.utils.book_append_sheet(wb, ws, '지점데이터');
    
    // 엑셀 파일 생성
    const excelBuffer = XLSXStyle.write(wb, { 
      bookType: 'xlsx', 
      type: 'array'
    });
    
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, '지점_업로드_샘플.xlsx');
    
    message.success('샘플 파일이 다운로드되었습니다.');
  };

  // DB와 중복 체크 함수
  const handleRecheckDuplicates = (companyId: string, data: any[]) => {
    const duplicatesInFileSet = new Set<string>();
    const duplicatesInDBSet = new Set<string>();
    const duplicateInfo: string[] = [];
    
    // 파일 내 중복 체크
    const branchNameCount = new Map<string, number[]>();
    data.forEach((item) => {
      const normalizedName = item.branch_name.toLowerCase().trim();
      if (!branchNameCount.has(normalizedName)) {
        branchNameCount.set(normalizedName, []);
      }
      branchNameCount.get(normalizedName)!.push(item.rowIndex);
    });
    
    branchNameCount.forEach((rows, normalizedName) => {
      if (rows.length > 1) {
        const originalNames = data
          .filter(item => item.branch_name.toLowerCase().trim() === normalizedName)
          .map(item => item.branch_name);
        const uniqueOriginals = Array.from(new Set<string>(originalNames));
        uniqueOriginals.forEach(name => duplicatesInFileSet.add(name));
        duplicateInfo.push(`[파일 내] "${uniqueOriginals.join(', ')}" - ${rows.join(', ')}행에 중복`);
      }
    });
    
    // DB와의 중복 체크
    const duplicatesInDB: string[] = [];
    if (companyId) {
      const existingBranches = branches.filter(b => b.company_id === companyId);
      const existingBranchNamesNormalized = new Set(
        existingBranches.map(b => b.branch_name.toLowerCase().trim())
      );
      
      data.forEach(item => {
        const normalizedName = item.branch_name.toLowerCase().trim();
        if (existingBranchNamesNormalized.has(normalizedName)) {
          duplicatesInDBSet.add(item.branch_name);
          if (!duplicatesInDB.some(d => d.includes(item.branch_name))) {
            duplicatesInDB.push(`[DB] "${item.branch_name}" - 이미 등록된 지점`);
          }
        }
      });
    }
    
    // 전체 중복 Set 합치기
    const allDuplicates = new Set([
      ...Array.from(duplicatesInFileSet),
      ...Array.from(duplicatesInDBSet)
    ]);
    setDuplicatesInFile(allDuplicates);
    
    // 상태 메시지 설정
    const allDuplicateInfo = [...duplicateInfo, ...duplicatesInDB];
    
    if (allDuplicateInfo.length > 0) {
      setUploadStatus({
        type: 'warning',
        message: `${data.length}개 데이터 중 ${allDuplicates.size}개 지점명이 중복됩니다.`,
        duplicates: allDuplicateInfo
      });
    } else {
      setUploadStatus({
        type: 'success',
        message: `${data.length}개의 지점 데이터가 준비되었습니다.`,
        duplicates: []
      });
    }
  };

  // 엑셀 파일 업로드 처리
  const handleExcelUpload = async (file: any) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // 데이터 검증
        const validatedData = jsonData.map((row: any, index: number) => {
          if (!row['지점명'] || row['지점명'].trim() === '') {
            throw new Error(`${index + 2}행: 지점명이 비어있습니다.`);
          }
          return {
            branch_name: row['지점명'].trim(),
            address: row['주소'] ? row['주소'].trim() : '',  // 주소는 선택사항
            rowIndex: index + 2,
          };
        });
        
        // 엑셀 내 지점명 중복 체크 (대소문자와 공백 정규화)
        const branchNameCount = new Map<string, { original: string, rows: number[] }>();
        const duplicatesInFileSet = new Set<string>();
        const duplicatesInDBSet = new Set<string>();
        
        validatedData.forEach((item) => {
          // 중복 체크를 위해 지점명을 정규화 (소문자 변환, 앞뒤 공백 제거)
          const normalizedName = item.branch_name.toLowerCase().trim();
          
          if (!branchNameCount.has(normalizedName)) {
            branchNameCount.set(normalizedName, { 
              original: item.branch_name, 
              rows: [] 
            });
          }
          branchNameCount.get(normalizedName)!.rows.push(item.rowIndex);
        });
        
        // 파일 내 중복 처리
        const duplicateInfo: string[] = [];
        branchNameCount.forEach((data, normalizedName) => {
          if (data.rows.length > 1) {
            // 원본 지점명들을 수집
            const originalNames = validatedData
              .filter(item => item.branch_name.toLowerCase().trim() === normalizedName)
              .map(item => item.branch_name);
            const uniqueOriginals = Array.from(new Set<string>(originalNames));
            
            uniqueOriginals.forEach(name => {
              duplicatesInFileSet.add(name);
            });
            
            const displayName = uniqueOriginals.join(', ');
            duplicateInfo.push(`[파일 내] "${displayName}" - ${data.rows.join(', ')}행에 중복`);
          }
        });
        
        // DB와의 중복 체크 (선택된 회사가 있을 때만)
        const duplicatesInDB: string[] = [];
        if (selectedCompanyForExcel) {
          // 해당 회사의 기존 지점들 가져오기
          const existingBranches = branches.filter(b => b.company_id === selectedCompanyForExcel);
          const existingBranchNamesNormalized = new Set(
            existingBranches.map(b => b.branch_name.toLowerCase().trim())
          );
          
          validatedData.forEach(item => {
            const normalizedName = item.branch_name.toLowerCase().trim();
            if (existingBranchNamesNormalized.has(normalizedName)) {
              duplicatesInDBSet.add(item.branch_name);
              if (!duplicatesInDB.some(d => d.includes(item.branch_name))) {
                duplicatesInDB.push(`[DB] "${item.branch_name}" - 이미 등록된 지점`);
              }
            }
          });
        }
        
        // 전체 중복 Set 합치기
        const allDuplicates = new Set([
          ...Array.from(duplicatesInFileSet),
          ...Array.from(duplicatesInDBSet)
        ]);
        setDuplicatesInFile(allDuplicates);
        setExcelData(validatedData);
        
        // 상태 메시지 설정
        const allDuplicateInfo = [...duplicateInfo, ...duplicatesInDB];
        
        if (allDuplicateInfo.length > 0) {
          setUploadStatus({
            type: 'warning',
            message: `${validatedData.length}개 데이터 중 ${allDuplicates.size}개 지점명이 중복됩니다.`,
            duplicates: allDuplicateInfo
          });
        } else {
          setUploadStatus({
            type: 'success',
            message: `${validatedData.length}개의 지점 데이터가 준비되었습니다.`,
            duplicates: []
          });
        }
      } catch (error: any) {
        logger.error('엑셀 파싱 오류:', error);
        message.error(error.message || '엑셀 파일을 읽는 중 오류가 발생했습니다.');
        setExcelData([]);
        setUploadStatus({
          type: 'error',
          message: error.message || '엑셀 파일을 읽는 중 오류가 발생했습니다.'
        });
      }
    };
    reader.readAsBinaryString(file);
    return false; // 자동 업로드 방지
  };

  // 엑셀 업로드 모달 열기
  const handleOpenExcelUpload = () => {
    // 모달 열기 전에 초기화
    setExcelData([]);
    setIsCompanyPreselected(false);
    setDuplicatesInFile(new Set());
    setUploadStatus({ type: 'idle' });
    setUploadKey(prev => prev + 1); // Upload 컴포넌트 리셋
    
    // 선택된 노드가 회사인 경우 자동으로 해당 회사 선택
    let selectedCompany = '';
    if (selectedNode.startsWith('company-')) {
      const companyId = selectedNode.replace('company-', '');
      selectedCompany = companyId;
      setSelectedCompanyForExcel(companyId);
      setIsCompanyPreselected(true);  // 조직도에서 선택했으므로 변경 불가
    } else if (selectedNode.startsWith('branch-')) {
      // 지점이 선택된 경우 해당 지점의 회사를 선택
      const branchId = selectedNode.replace('branch-', '');
      const branch = branches.find(b => b.id === branchId);
      if (branch) {
        selectedCompany = branch.company_id;
        setSelectedCompanyForExcel(branch.company_id);
        setIsCompanyPreselected(true);  // 조직도에서 선택했으므로 변경 불가
      }
    } else {
      setSelectedCompanyForExcel('');
    }
    
    setExcelUploadModalVisible(true);
  };

  // 엑셀 데이터 일괄 등록
  const handleBulkCreate = async () => {
    if (!selectedCompanyForExcel) {
      message.error('회사를 선택해주세요.');
      return;
    }
    
    if (excelData.length === 0) {
      message.error('등록할 데이터가 없습니다.');
      return;
    }
    
    // 파일 내 중복 제거 (첫 번째 항목만 유지)
    let dataToProcess = excelData;
    if (duplicatesInFile.size > 0) {
      const seen = new Set<string>();
      dataToProcess = excelData.filter(item => {
        if (seen.has(item.branch_name)) {
          return false;
        }
        seen.add(item.branch_name);
        return true;
      });
      message.info(`파일 내 중복 ${excelData.length - dataToProcess.length}개 제외하고 ${dataToProcess.length}개 지점을 등록합니다.`);
    }
    
    setUploadLoading(true);
    let successCount = 0;
    let failCount = 0;
    const errors: string[] = [];
    
    // processBulkCreate 함수를 내부에 정의
    async function processBulkCreate(data: any[]) {
      for (const item of data) {
        try {
          await OrganizationService.createBranch({
            branch_name: item.branch_name,
            company_id: selectedCompanyForExcel,
            address: item.address,
          });
          successCount++;
        } catch (error: any) {
          failCount++;
          errors.push(`${item.rowIndex}행 (${item.branch_name}): ${error.message}`);
        }
      }
      
      if (successCount > 0) {
        notification.success({
          message: '일괄 등록 완료',
          description: `${successCount}개의 지점이 등록되었습니다.`,
        });
      }
      
      if (failCount > 0) {
        Modal.error({
          title: '일부 등록 실패',
          content: (
            <div>
              <p>{failCount}개의 지점 등록에 실패했습니다.</p>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {errors.map((err, idx) => (
                  <div key={idx} style={{ fontSize: '12px', color: '#ff4d4f' }}>
                    {err}
                  </div>
                ))}
              </div>
            </div>
          ),
        });
      }
      
      setExcelUploadModalVisible(false);
      setExcelData([]);
      setSelectedCompanyForExcel('');
      setDuplicatesInFile(new Set());
      setUploadStatus({ type: 'idle' });
      await loadData();
    }
    
    try {
      // 먼저 기존 지점명 목록 가져오기
      const existingBranches = branches.filter(b => b.company_id === selectedCompanyForExcel);
      const existingBranchNames = new Set(existingBranches.map(b => b.branch_name));
      
      // 중복 체크
      const duplicatesInDB: string[] = [];
      dataToProcess.forEach(item => {
        if (existingBranchNames.has(item.branch_name)) {
          duplicatesInDB.push(`${item.rowIndex}행: ${item.branch_name}`);
        }
      });
      
      if (duplicatesInDB.length > 0) {
        Modal.confirm({
          title: '⚠️ 중복된 지점명 발견',
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          content: (
            <div>
              <Alert
                message="중복 확인"
                description={
                  <>
                    <p>다음 지점명이 이미 등록되어 있습니다:</p>
                    <div style={{ 
                      maxHeight: '150px', 
                      overflowY: 'auto',
                      border: '1px solid #ffd591',
                      borderRadius: '4px',
                      padding: '8px',
                      marginTop: '8px',
                      backgroundColor: '#fffbe6'
                    }}>
                      {duplicatesInDB.map((dup, idx) => (
                        <div key={idx} style={{ 
                          fontSize: '13px', 
                          color: '#d46b08',
                          padding: '2px 0'
                        }}>
                          • {dup}
                        </div>
                      ))}
                    </div>
                    <Divider />
                    <p style={{ fontWeight: 'bold', color: '#1890ff' }}>
                      중복된 {duplicatesInDB.length}개를 제외한 나머지 {dataToProcess.length - duplicatesInDB.length}개의 지점만 등록됩니다.
                    </p>
                    <p>계속 진행하시겠습니까?</p>
                  </>
                }
                type="warning"
                showIcon
                style={{ marginTop: '12px' }}
              />
            </div>
          ),
          okText: '중복 제외하고 진행',
          cancelText: '취소',
          okButtonProps: { type: 'primary' },
          width: 500,
          onOk: async () => {
            // 중복된 항목 제외하고 진행
            const filteredData = dataToProcess.filter(item => 
              !existingBranchNames.has(item.branch_name)
            );
            await processBulkCreate(filteredData);
          },
          onCancel: () => {
            message.info('업로드가 취소되었습니다.');
            setUploadLoading(false);
          },
        });
        return;
      }
      
      await processBulkCreate(dataToProcess);
    } catch (error: any) {
      logger.error('일괄 등록 오류:', error);
      message.error('일괄 등록 중 오류가 발생했습니다.');
      setUploadLoading(false);
    }
  };

  const { filteredCompanies, filteredBranches, filteredUsers } = getFilteredData();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="organization-management">
      <h2>조직 관리</h2>
      
      <Row gutter={16}>
        {/* 왼쪽: 조직도 트리 */}
        <Col span={6}>
          <Card
            title={
              <Space>
                <BankOutlined />
                조직도
              </Space>
            }
            extra={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={loadData}
              />
            }
            className="org-tree-card"
          >
            <Tree
              treeData={getTreeData()}
              defaultExpandAll
              selectedKeys={[selectedNode]}
              onSelect={handleTreeSelect}
              showIcon
              className="org-tree"
            />
          </Card>
        </Col>

        {/* 오른쪽: 상세 정보 */}
        <Col span={18}>
          {/* 회사 관리 */}
          <Card
            title={
              <Space>
                <TeamOutlined />
                회사 관리
                <span style={{ color: '#999', fontSize: '14px' }}>({filteredCompanies.length})</span>
              </Space>
            }
            extra={
              currentUser?.is_admin && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCompany}
                >
                  회사 추가
                </Button>
              )
            }
            className="org-section-card"
          >
            {filteredCompanies.length > 0 ? (
              <Table
                columns={companyColumns}
                dataSource={filteredCompanies}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="등록된 회사가 없습니다." />
            )}
          </Card>

          {/* 지점 관리 */}
          <Card
            title={
              <Space>
                <ShopOutlined />
                지점 관리
                <span style={{ color: '#999', fontSize: '14px' }}>({filteredBranches.length})</span>
              </Space>
            }
            extra={
              currentUser?.is_admin && (
                <Space>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadSample}
                  >
                    샘플 다운로드
                  </Button>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={handleOpenExcelUpload}
                    disabled={companies.length === 0 || selectedNode === 'all'}
                    title={selectedNode === 'all' ? '회사를 선택해주세요' : '엑셀로 지점 일괄 등록'}
                  >
                    엑셀 업로드
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddBranch}
                    disabled={companies.length === 0}
                  >
                    지점 추가
                  </Button>
                </Space>
              )
            }
            className="org-section-card"
          >
            {filteredBranches.length > 0 ? (
              <Table
                columns={branchColumns}
                dataSource={filteredBranches}
                rowKey="id"
                pagination={{
                  ...branchPagination,
                  total: filteredBranches.length,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total) => `전체 ${total}건`,
                }}
                onChange={(pagination) => {
                  setBranchPagination({
                    current: pagination.current || 1,
                    pageSize: pagination.pageSize || 10,
                  });
                }}
                size="small"
              />
            ) : (
              <Empty description="등록된 지점이 없습니다." />
            )}
          </Card>

          {/* 사용자 현황 */}
          <Card
            title={
              <Space>
                <UserOutlined />
                사용자 현황
                <span style={{ color: '#999', fontSize: '14px' }}>({filteredUsers.length})</span>
              </Space>
            }
            className="org-section-card"
          >
            {filteredUsers.length > 0 ? (
              <Table
                columns={userColumns}
                dataSource={filteredUsers}
                rowKey="id"
                pagination={{
                  ...userPagination,
                  total: filteredUsers.length,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total) => `전체 ${total}건`,
                }}
                onChange={(pagination) => {
                  setUserPagination({
                    current: pagination.current || 1,
                    pageSize: pagination.pageSize || 10,
                  });
                }}
                size="small"
              />
            ) : (
              <Empty description="등록된 사용자가 없습니다." />
            )}
          </Card>
        </Col>
      </Row>

      {/* 회사 추가/수정 모달 */}
      <Modal
        title={editingCompany ? '회사 수정' : '회사 추가'}
        open={companyModalVisible}
        onCancel={() => setCompanyModalVisible(false)}
        footer={null}
      >
        <Form
          form={companyForm}
          layout="vertical"
          onFinish={handleSaveCompany}
        >
          <Form.Item
            label="회사명"
            name="name"
            rules={[{ required: true, message: '회사명을 입력해주세요' }]}
          >
            <Input placeholder="예: 크린존" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCompany ? '수정' : '추가'}
              </Button>
              <Button onClick={() => setCompanyModalVisible(false)}>
                취소
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 지점 추가/수정 모달 */}
      <Modal
        title={editingBranch ? '지점 수정' : '지점 추가'}
        open={branchModalVisible}
        onCancel={() => setBranchModalVisible(false)}
        footer={null}
      >
        <Form
          form={branchForm}
          layout="vertical"
          onFinish={handleSaveBranch}
        >
          <Form.Item
            label="소속 회사"
            name="company_id"
            rules={[{ required: true, message: '소속 회사를 선택해주세요' }]}
          >
            <Select
              disabled={!!editingBranch || (selectedNode !== 'all')}
              showSearch
              optionFilterProp="children"
            >
              {companies.map(company => (
                <Select.Option key={company.id} value={company.id}>
                  {company.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="지점명"
            name="branch_name"
            rules={[{ required: true, message: '지점명을 입력해주세요' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="주소"
            name="address"
          >
            <Input.TextArea
              rows={2}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingBranch ? '수정' : '추가'}
              </Button>
              <Button onClick={() => setBranchModalVisible(false)}>
                취소
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 엑셀 업로드 모달 */}
      <Modal
        title={
          <Space>
            <FileExcelOutlined />
            지점 엑셀 일괄 등록
          </Space>
        }
        open={excelUploadModalVisible}
        onCancel={() => {
          setExcelUploadModalVisible(false);
          setExcelData([]);
          setSelectedCompanyForExcel('');
          setIsCompanyPreselected(false);
          setDuplicatesInFile(new Set());
          setUploadStatus({ type: 'idle' });
        }}
        afterClose={() => {
          // 모달 완전히 닫힌 후 초기화
          setExcelData([]);
          setDuplicatesInFile(new Set());
          setUploadStatus({ type: 'idle' });
          setUploadKey(prev => prev + 1); // Upload 컴포넌트 리셋
          // 회사 선택은 다음 열기 때 결정되므로 여기서는 초기화하지 않음
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setExcelUploadModalVisible(false);
              setExcelData([]);
              setSelectedCompanyForExcel('');
              setIsCompanyPreselected(false);
              setDuplicatesInFile(new Set());
              setUploadStatus({ type: 'idle' });
            }}
          >
            취소
          </Button>,
          <Tooltip 
            title={duplicatesInFile.size > 0 ? "중복된 지점명을 먼저 제거해주세요." : ""}
          >
            <Button
              key="submit"
              type="primary"
              loading={uploadLoading}
              onClick={handleBulkCreate}
              disabled={excelData.length === 0 || !selectedCompanyForExcel || duplicatesInFile.size > 0}
            >
              일괄 등록
            </Button>
          </Tooltip>,
        ]}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* 안내 메시지는 파일 업로드 전에만 표시 */}
          {excelData.length === 0 && uploadStatus.type === 'idle' && (
            <Alert
              message="안내"
              description={
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>엑셀 파일에는 '지점명' 컬럼이 필수입니다.</li>
                  <li>'주소' 컬럼은 선택사항입니다.</li>
                  <li>샘플 파일을 다운로드하여 양식을 확인해주세요.</li>
                  <li>선택한 회사에 지점들이 일괄 등록됩니다.</li>
                  <li>중복된 지점명은 등록에 실패합니다.</li>
                </ul>
              }
              type="info"
              showIcon
            />
          )}
          
          <Form layout="vertical">
            <Form.Item
              label="소속 회사"
              required
            >
              <Select
                placeholder="지점을 등록할 회사를 선택해주세요"
                value={selectedCompanyForExcel}
                onChange={(value) => {
                  setSelectedCompanyForExcel(value);
                  // 회사 변경 시 엑셀 데이터가 있으면 DB 중복 다시 체크
                  if (excelData.length > 0) {
                    handleRecheckDuplicates(value, excelData);
                  }
                }}
                style={{ width: '100%' }}
                disabled={isCompanyPreselected}  // 조직도에서 선택한 경우 변경 불가
              >
                {companies.map(company => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item
              label="엑셀 파일"
              required
              help={!selectedCompanyForExcel ? "회사를 먼저 선택해주세요" : ""}
            >
              <Upload
                key={uploadKey}
                accept=".xlsx,.xls"
                beforeUpload={handleExcelUpload}
                maxCount={1}
                fileList={[]}
                disabled={!selectedCompanyForExcel}
                onRemove={() => {
                  setExcelData([]);
                  setDuplicatesInFile(new Set());
                  setUploadStatus({ type: 'idle' });
                }}
              >
                <Button icon={<UploadOutlined />}>
                  엑셀 파일 선택
                </Button>
              </Upload>
            </Form.Item>
          </Form>
          
          {/* 업로드 상태 표시 - 단일 통합 알림 */}
          {(() => {
            // 상태에 따른 단일 알림 표시
            if (uploadStatus.type === 'error') {
              return (
                <Alert
                  message="오류 발생"
                  description={uploadStatus.message}
                  type="error"
                  showIcon
                  closable
                  style={{ marginBottom: '16px' }}
                />
              );
            }
            
            if (excelData.length > 0) {
              const hasDuplicates = duplicatesInFile.size > 0;
              
              // 중복된 지점명의 개수를 정확히 계산
              const duplicateCount = Array.from(duplicatesInFile).length;
              
              return (
                <Alert
                  message={
                    hasDuplicates 
                      ? `${excelData.length}개 데이터 중 ${duplicateCount}개 지점명이 중복됩니다.`
                      : `${excelData.length}개의 지점 데이터가 준비되었습니다.`
                  }
                  description={
                    hasDuplicates ? 
                      "중복된 지점명이 있어 일괄등록 버튼이 비활성화됩니다." :
                      "중복이 없습니다. 일괄등록 버튼을 클릭하여 진행하세요."
                  }
                  type={hasDuplicates ? 'warning' : 'success'}
                  showIcon
                  closable={false}
                  style={{ marginBottom: '16px' }}
                />
              );
            }
            
            return null;
          })()}
          
          {excelData.length > 0 && (
            <Table
                columns={[
                  {
                    title: '번호',
                    dataIndex: 'rowIndex',
                    key: 'rowIndex',
                    width: 60,
                  },
                  {
                    title: '지점명',
                    dataIndex: 'branch_name',
                    key: 'branch_name',
                    render: (text: string) => {
                      const isDuplicate = duplicatesInFile.has(text);
                      return (
                        <span style={{ 
                          color: isDuplicate ? '#ff7a45' : 'inherit',
                          fontWeight: isDuplicate ? 'bold' : 'normal'
                        }}>
                          {isDuplicate && <ExclamationCircleOutlined style={{ marginRight: '4px', color: '#ff7a45' }} />}
                          {text}
                        </span>
                      );
                    }
                  },
                  {
                    title: '주소',
                    dataIndex: 'address',
                    key: 'address',
                    ellipsis: true,
                  },
                  {
                    title: '상태',
                    key: 'status',
                    width: 80,
                    render: (_: any, record: any) => {
                      const isDuplicate = duplicatesInFile.has(record.branch_name);
                      return (
                        <Tag color={isDuplicate ? 'warning' : 'success'}>
                          {isDuplicate ? '중복' : '정상'}
                        </Tag>
                      );
                    }
                  },
                ]}
                dataSource={excelData}
                rowKey="rowIndex"
                size="small"
                pagination={{
                  pageSize: 5,
                  showSizeChanger: false,
                }}
                scroll={{ y: 200 }}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default OrganizationManagement;